/**
 * This store is used for toggling/flipping features based on store state.
 *
 * To force enable a feature you can add "?<featureId>=true" as a query parameter to the url.
 *
 * Use this store in your component like so:
 *
 * @example
 * <template>
 *  <div v-if="isTestFeatureEnabled">
 *    ...
 *   </div>
 * </template>
 *
 * <script>
 *  computed: {
 *    ...mapGetters({
 *       isFeatureEnabled: 'featureToggle/isEnabled',
 *     }),
 *
 *    isTestFeatureEnabled() {
 *      return this.isFeatureEnabled('testFeatureId')
 *    },
 *  },
 *
 *  // consider adding this in App.vue
 *  beforeCreate() {
 *    this.addFeature({
 *      id: 'testFeatureId'
 *      condition: (rootState, rootGetters) => rootState.myStore.isMyConditionSatisfied
 *    })
 *  },
 *  methods: {
 *    ...mapMutations({
 *      addFeature: 'featureToggle/addFeature',
 *    }),
 *  }
 * </script>
 *
 */
import Vue from 'vue'

function isFeatureValid(feature) {
  if (!feature.id) throw new Error('feature property id is undefined')
  if (!feature.condition) throw new Error('feature property condition is undefined')
  return true
}

export default {
  namespaced: true,
  state: {
    features: {},
  },
  mutations: {
    addFeature(state, feature) {
      if (isFeatureValid(feature)) Vue.set(state.features, feature.id, feature)
    },
  },
  getters: {
    isEnabled: (state, getters, rootState, rootGetters) => featureId => {
      const feature = state.features[featureId]
      if (!feature) return false
      const isForceEnabled = getters.isForceEnabled(featureId)
      return isForceEnabled || feature.condition(rootState, rootGetters)
    },
    isForceEnabled: (state, getters, rootState) => featureId =>
      rootState.route.query[featureId] === 'true',
  },
}
